import React, { useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Image,
} from '../components/rn';
import PrimaryActionButton from '../components/PrimaryActionButton';
import * as ImagePicker from 'expo-image-picker';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { api, ApiError, NetworkError, createProfilePhotoAttempt, type ProfilePhotoAttempt } from '../services/api';

type Props = {
  token: string;
  universityId?: number;
  iscedLevel?: number;
  professionId?: number;
  onContinue: () => void;
};

export default function ProfileScreen({ token, universityId, iscedLevel, professionId, onContinue }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const busy = useRef(false);
  const completed = useRef(new Set<string>());
  const photoAttempt = useRef<ProfilePhotoAttempt | null>(null);
  const [picking, setPicking] = useState(false);

  async function pickPhoto() {
    if (busy.current) return;
    busy.current = true;
    setPicking(true);
    try {
      const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
      if (!result.canceled && result.assets[0]?.uri) {
        // A new selection is a new upload even when the picker reuses its URI.
        completed.current.delete(JSON.stringify([token, 'photo', result.assets[0].uri]));
        photoAttempt.current = createProfilePhotoAttempt(token);
        setPhotoUri(result.assets[0].uri);
        setPhotoFile(result.assets[0].file ?? null);
      }
    } catch {
      Alert.alert(t('common.error'), t('profileCreate.photoPickerFailed'));
    } finally {
      busy.current = false;
      setPicking(false);
    }
  }

  async function handleContinue() {
    if (busy.current || !name.trim()) return;
    busy.current = true;
    setLoading(true);
    try {
      // Keep acknowledged successes across retries, without claiming a transaction.
      async function once(key: unknown[], write: () => Promise<unknown>) {
        const id = JSON.stringify([token, ...key]);
        if (completed.current.has(id)) return;
        await write();
        completed.current.add(id);
      }
      if (photoUri) {
        if (!photoAttempt.current || photoAttempt.current.token !== token) photoAttempt.current = createProfilePhotoAttempt(token);
        await once(['photo', photoUri], () => api.uploadPhoto(token, photoUri, photoFile ?? undefined, photoAttempt.current!));
      }
      // Never fall back to separate inserts after an ambiguous network failure.
      // The server can replay this desired state even if its commit response was lost.
      await api.completeOnboardingProfile(token, {
        name: name.trim(),
        surname: surname.trim() || undefined,
        ...(universityId && iscedLevel ? { university_id: universityId, isced_level: iscedLevel } : {}),
        ...(professionId ? { profession_id: professionId } : {}),
      });
      onContinue();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'photo_upload_conflict') {
        Alert.alert(t('common.error'), t('profile.photoChanged'));
      } else if (error instanceof NetworkError) {
        Alert.alert(t('message.noConnection'), t('message.checkInternet'));
      } else {
        Alert.alert(t('common.error'), t('profile.saveFailed'));
      }
    } finally {
      busy.current = false;
      setLoading(false);
    }
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
      <View style={styles.inner}>
        <Text style={styles.title}>{t('profileCreate.title')}</Text>

        <TouchableOpacity testID="onboarding-profile-photo" style={styles.avatarWrapper} onPress={pickPhoto} disabled={loading || picking}>
          {photoUri ? (
            <Image source={{ uri: photoUri }} style={styles.avatar} />
          ) : (
            <View style={styles.avatarPlaceholder}>
              <Text style={styles.avatarPlaceholderText}>{t('profileCreate.addPhoto')}</Text>
            </View>
          )}
        </TouchableOpacity>

        <TextInput
          style={styles.input}
          placeholder={t('profileCreate.name')}
          placeholderTextColor={colors.gray400}
          value={name}
          editable={!loading}
          onChangeText={setName}
          autoCorrect={false}
        />

        <TextInput
          style={styles.input}
          placeholder={t('profileCreate.surname')}
          placeholderTextColor={colors.gray400}
          value={surname}
          editable={!loading}
          onChangeText={setSurname}
          autoCorrect={false}
        />

        <PrimaryActionButton
          testID="onboarding-profile-continue"
          label={t('common.continue')}
          disabled={!name.trim() || loading || picking}
          loading={loading}
          onPress={handleContinue}
        />
      </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: spacing.xl,
  },
  inner: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl * 3,
    alignItems: 'center',
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '700',
    color: colors.black,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  avatarWrapper: {
    marginBottom: spacing.xl,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  avatarPlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: colors.gray100,
    borderWidth: 1,
    borderColor: colors.gray200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarPlaceholderText: {
    fontSize: fontSize.sm,
    color: colors.gray400,
    textAlign: 'center',
  },
  input: {
    fontSize: fontSize.lg,
    color: colors.black,
    borderBottomWidth: 1,
    borderBottomColor: colors.black,
    paddingVertical: spacing.sm,
    marginBottom: spacing.xl,
    alignSelf: 'stretch',
  },
});
